import axiosInstance from './axiosInstance';

export enum FileType {
    CV = 'CV',
    CERTIFICATION = 'CERTIFICATION',
    PROFILE_PICTURE = 'PROFILE_PICTURE',
    WORK_PICTURE = 'WORK_PICTURE',
    REFERENCE = 'REFERENCE',
    RIJBEWIJS = 'RIJBEWIJS',
    ID_DOCUMENT = 'ID_DOCUMENT',
    // VCA safety passport — extracted via the dedicated /documents/extract-vca
    // pipeline. Backend stores VCA sub-type (BASIS / VOL / VIL) + number +
    // dates on WorkerEntity.vca* columns once the recruiter / worker confirms.
    VCA = 'VCA',
    // Recruiter-generated NAW PDF. Saved to worker_files by the NAW generator.
    NAW_DOCUMENT = 'NAW_DOCUMENT',
    OTHER = 'OTHER'
}

export enum FileSource {
    WEB_UPLOAD = 'WEB_UPLOAD',
    WHATSAPP = 'WHATSAPP',
    EMAIL = 'EMAIL',
    API = 'API',
    // App-produced file (e.g. the AI CV generator's PDF). Hides re-parsing.
    GENERATED = 'GENERATED'
}

export enum FileParseStatus {
    PENDING = 'PENDING',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    FAILED = 'FAILED',
    PARTIAL = 'PARTIAL'
}

export interface WorkerFile {
    id: number;
    workerId: string;
    fileType: FileType;
    fileName: string;
    originalFileName: string;
    mimeType: string;
    sizeBytes: number;
    filePath: string;
    description?: string;
    uploadedBy: string;
    uploadedByName: string;
    lastModifiedBy?: string;
    lastModifiedByName?: string;
    isParsed: boolean;
    lastParsedAt?: string;
    parseCount: number;
    source: FileSource;
    whatsappMediaId?: string;
    createdAt: string;
    updatedAt: string;
}

export type ParseStatus = 'IN_PROGRESS' | 'SUCCESS' | 'PARTIAL' | 'FAILED';
export type ParseSource = 'WHATSAPP' | 'DASHBOARD';

export interface FileParseHistory {
    id: number;
    workerFileId: number;
    workerId: string;
    status: ParseStatus;
    source: ParseSource;
    questionsExtracted: number;
    totalQuestions: number;
    parsedData?: Record<string, any>;
    errorMessage?: string;
    jobId?: string;
    parsedBy: string;
    parsedByName: string;
    duration?: number;
    createdAt: string;
}

export interface UploadFileData {
    file: File;
    workerId: string;
    fileType: FileType;
    description?: string;
}

export interface UpdateFileData {
    fileType?: FileType;
    description?: string;
}

// Upload a file for a worker.
// `onProgress` is called with 0..100 as bytes are transferred; useful for
// driving a progress bar on multi-MB uploads.
export const uploadWorkerFile = async (
    data: UploadFileData,
    onProgress?: (percent: number) => void,
): Promise<WorkerFile> => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('workerId', data.workerId);
    formData.append('fileType', data.fileType);
    if (data.description) {
        formData.append('description', data.description);
    }

    const response = await axiosInstance.post<WorkerFile>('/worker-files/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: onProgress
            ? (event) => {
                  // event.total may be undefined when the server doesn't expose
                  // Content-Length on the request (rare for FormData but defensive).
                  if (!event.total) return;
                  onProgress(Math.round((event.loaded / event.total) * 100));
              }
            : undefined,
    });
    return response.data;
};

// Get all files for a worker
export const getWorkerFiles = async (workerId: string): Promise<WorkerFile[]> => {
    const response = await axiosInstance.get<WorkerFile[]>(`/worker-files/worker/${workerId}`);
    return response.data;
};

// Get files by type for a worker
export const getWorkerFilesByType = async (workerId: string, fileType: FileType): Promise<WorkerFile[]> => {
    const response = await axiosInstance.get<WorkerFile[]>(`/worker-files/worker/${workerId}/type/${fileType}`);
    return response.data;
};

// Get file metadata by ID
export const getWorkerFile = async (fileId: number): Promise<WorkerFile> => {
    const response = await axiosInstance.get<WorkerFile>(`/worker-files/${fileId}`);
    return response.data;
};

// Download a file
export const downloadWorkerFile = async (fileId: number): Promise<Blob> => {
    const response = await axiosInstance.get(`/worker-files/${fileId}/download`, {
        responseType: 'blob',
    });
    return response.data;
};

// Preview a file (fetch content with authentication)
export const previewWorkerFile = async (fileId: number): Promise<Blob> => {
    const response = await axiosInstance.get(`/worker-files/${fileId}/preview`, {
        responseType: 'blob',
    });
    return response.data;
};

// Update file metadata
export const updateWorkerFile = async (fileId: number, data: UpdateFileData): Promise<WorkerFile> => {
    const response = await axiosInstance.put<WorkerFile>(`/worker-files/${fileId}`, data);
    return response.data;
};

// Delete a file
export const deleteWorkerFile = async (fileId: number): Promise<void> => {
    await axiosInstance.delete(`/worker-files/${fileId}`);
};

// Parse a CV file
// @deprecated Routes through the bot pipeline — fires WhatsApp messages to
// the worker. Use `parseWorkerFileForReview` for recruiter-initiated parses.
export const parseWorkerFile = async (fileId: number): Promise<{ jobId: string; message: string }> => {
    const response = await axiosInstance.post<{ jobId: string; message: string }>(`/worker-files/${fileId}/parse`);
    return response.data;
};

/** Recruiter-triggered parse. Silent — no worker WhatsApp message, no FSM
 * transition. Result lands in `parse_history.parsedData` and is delivered to
 * the modal via the `fileParseCompleted` socket event (or polling). */
export const parseWorkerFileForReview = async (
    fileId: number,
): Promise<{ jobId: string; message: string }> => {
    const response = await axiosInstance.post<{ jobId: string; message: string }>(
        `/worker-files/${fileId}/parse-for-review`,
    );
    return response.data;
};

// Get parse history for a file
export const getFileParseHistory = async (fileId: number): Promise<FileParseHistory[]> => {
    const response = await axiosInstance.get<FileParseHistory[]>(`/worker-files/${fileId}/parse-history`);
    return response.data;
};

/** Fetch a specific parse result by jobId. Used by the review modal as a
 * polling fallback when the `fileParseCompleted` socket event doesn't land
 * (e.g. dev-tools tab without socket connection). */
export const getParseResult = async (
    fileId: number,
    jobId: string,
): Promise<FileParseHistory | null> => {
    const response = await axiosInstance.get<FileParseHistory | null>(
        `/worker-files/${fileId}/parse-result/${encodeURIComponent(jobId)}`,
    );
    return response.data;
};

// Type aliases for backwards compatibility
export type WorkerFileResponse = WorkerFile;
export type FileParseHistoryResponse = FileParseHistory;
export type WorkerFileType = FileType;

// Function aliases for backwards compatibility
export const parseFile = parseWorkerFile;
export const deleteFile = deleteWorkerFile;
export const downloadFile = downloadWorkerFile;

// Upload multiple files
export const uploadMultipleWorkerFiles = async (files: UploadFileData[]): Promise<WorkerFile[]> => {
    const uploadPromises = files.map(fileData => uploadWorkerFile(fileData));
    return Promise.all(uploadPromises);
};

// Utility functions
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileTypeIcon = (fileType: FileType): string => {
    switch (fileType) {
        case FileType.CV:
            return 'Description';
        case FileType.CERTIFICATION:
            return 'CardMembership';
        case FileType.RIJBEWIJS:
            return 'DirectionsCar';
        case FileType.ID_DOCUMENT:
            return 'Badge';
        case FileType.VCA:
            return 'HealthAndSafety';
        case FileType.NAW_DOCUMENT:
            return 'ArticleOutlined';
        case FileType.REFERENCE:
            return 'CollectionsBookmark';
        case FileType.OTHER:
        default:
            return 'InsertDriveFile';
    }
};

export const FILE_TYPE_LABELS: Record<FileType, string> = {
    [FileType.CV]: 'CV/Resume',
    [FileType.CERTIFICATION]: 'Certification',
    [FileType.PROFILE_PICTURE]: 'Profile Picture',
    [FileType.WORK_PICTURE]: 'Work Picture',
    [FileType.REFERENCE]: 'Reference / Work Portfolio',
    [FileType.RIJBEWIJS]: 'Rijbewijs',
    [FileType.ID_DOCUMENT]: 'ID Document',
    [FileType.VCA]: 'VCA Safety Passport',
    [FileType.NAW_DOCUMENT]: 'NAW Document',
    [FileType.OTHER]: 'Other',
};

// ─── VCA + ID extraction (recruiter-triggered) ─────────────────────────
//
// Two endpoints, both sync wrappers around optima-ai's `/documents/extract-*`.
// Backend reads the stored file bytes, calls Gemini Vision, returns extracted
// fields verbatim. The recruiter reviews / edits in the confirmation modal,
// then PATCHes the worker entity via the existing PUT /workers/:workerId.

export interface VcaExtractionFields {
    vca_type: 'BASIS' | 'VOL' | 'VIL' | null;
    vca_number: string | null;
    issue_date: string | null;
    valid_until: string | null;
    confidence: number;
}

export interface IdExtractionFields {
    id_type: 'PASSPORT' | 'ID_CARD' | 'RESIDENCE_PERMIT' | 'DRIVERS_LICENSE' | null;
    id_number: string | null;
    valid_from: string | null;
    valid_until: string | null;
    confidence: number;
}

export const extractVcaFromFile = async (fileId: number): Promise<VcaExtractionFields> => {
    const response = await axiosInstance.post<VcaExtractionFields>(
        `/worker-files/${fileId}/extract-vca`,
    );
    return response.data;
};

export const extractIdFromFile = async (fileId: number): Promise<IdExtractionFields> => {
    const response = await axiosInstance.post<IdExtractionFields>(
        `/worker-files/${fileId}/extract-id`,
    );
    return response.data;
};
